import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useNavigation } from "../contexts/NavigationContext";
import { useLanguage } from "../contexts/LanguageContext";
import { fetchEvents } from "../services/eventService";
import { fetchCatholicFestivities } from "../services/catholicFestivityService";
import CalendarView from "./CalendarView";
import EventCard from "./event/desktop/event_card/EventCard";
import MobileEventView from "./event/mobile/MobileEventView"; // Keep original component name
import MobileEventsList from "./MobileEventsList";
import EventFormModal from "./event/desktop/event_modal/EventFormModal";
import DeleteEventButton from "./event/desktop/event_modal/DeleteEventButton";
import "./UserDashboard.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faChevronLeft, 
  faChevronRight, 
  faCalendarAlt, 
  faPlus
} from "@fortawesome/free-solid-svg-icons";

function UserDashboard() {
  const { user } = useAuth();
  const { isMobile } = useNavigation();
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  // State declarations
  const [allEvents, setAllEvents] = useState([]);
  const [currentEventIndex, setCurrentEventIndex] = useState(0);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentFestivities, setRecentFestivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [festivityEvent, setFestivityEvent] = useState(null);
  const [showNewEventModal, setShowNewEventModal] = useState(false);
  const [deleteError, setDeleteError] = useState("");
  // Add state for mobile tab management
  const [mobileActiveTab, setMobileActiveTab] = useState('setlist');

  // Handle event ID in URL and localStorage to persist through login redirection
  useEffect(() => {
    const checkForEventId = () => {
      // Check for event ID in URL
      const queryParams = new URLSearchParams(location.search);
      const queryEventId = queryParams.get("event");
      
      if (queryEventId) {
        // Only set selectedEventId if there's an explicit event in the URL
        setSelectedEventId(parseInt(queryEventId, 10));
      } else {
        // If there's no event ID in the URL, explicitly set selectedEventId to null
        setSelectedEventId(null);
        
        // IMPORTANT: Clear localStorage when explicitly viewing the list
        localStorage.removeItem('selectedEventId');
      }
    };
    
    // First handle the URL check
    checkForEventId();
    
    // Then load the data (separate from URL management)
    if (!isLoading) {  // Avoid duplicate loading on initial mount
      loadDashboardData();
    }
  }, [location.search]); // Re-run only when URL parameters change

  // Initial load of dashboard data
  useEffect(() => {
    loadDashboardData();
  }, []);

  // Centralized data loading function
  const loadDashboardData = async () => {
    setIsLoading(true);
    try {
      // Fetch and sort events by date
      const eventsData = await fetchEvents();
      const sortedEvents = eventsData.sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );
      setAllEvents(sortedEvents);

      // Determine today's date for filtering
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      // Find all upcoming events (today and future)
      const upcomingEvents = sortedEvents.filter((event) => {
        const eventDate = new Date(event.date);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate >= today;
      });

      // Check for a query parameter in the URL (for email links)
      const queryParams = new URLSearchParams(location.search);
      const queryEventId = queryParams.get("event");

      if (queryEventId) {
        const eventIdNum = parseInt(queryEventId, 10);
        const eventIndex = sortedEvents.findIndex(
          (event) => event.id === eventIdNum
        );
        if (eventIndex >= 0) {
          // Found the event from the query parameter
          setCurrentEventIndex(eventIndex);
        } else if (upcomingEvents.length > 0) {
          // If query event not found, show the next upcoming event
          const nextEventIndex = sortedEvents.findIndex(
            (event) => event.id === upcomingEvents[0].id
          );
          setCurrentEventIndex(nextEventIndex >= 0 ? nextEventIndex : 0);
          // Invalid event ID - redirect to the list
          navigate('/dashboard', { replace: true });
        } else {
          // If no upcoming events, show the first event
          setCurrentEventIndex(sortedEvents.length > 0 ? 0 : -1);
          // Invalid event ID - redirect to the list
          navigate('/dashboard', { replace: true });
        }
      } else {
        // No query parameter: Default to showing the events list in mobile view
        if (upcomingEvents.length > 0) {
          const nextEventIndex = sortedEvents.findIndex(
            (event) => event.id === upcomingEvents[0].id
          );
          setCurrentEventIndex(nextEventIndex >= 0 ? nextEventIndex : 0);
        } else {
          // If no upcoming events, show the first event
          setCurrentEventIndex(sortedEvents.length > 0 ? 0 : -1);
        }
      }
      
      // For the upcoming events section, set events in the next week
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 7);
      const nextWeekEvents = sortedEvents.filter((event) => {
        const eventDate = new Date(event.date);
        eventDate.setHours(0, 0, 0, 0);
        return eventDate >= today && eventDate <= nextWeek;
      });
      setUpcomingEvents(nextWeekEvents.slice(0, 5));

      // Fetch festivities for the current month
      const currentYear = today.getFullYear();
      const currentMonth = today.getMonth() + 1;
      const festivities = await fetchCatholicFestivities(currentYear, currentMonth);
      const relevantFestivities = festivities.filter((festivity) => {
        const festivityDate = new Date(festivity.date);
        const threeDaysAgo = new Date(today);
        threeDaysAgo.setDate(today.getDate() - 3);
        return festivityDate >= threeDaysAgo && festivityDate <= nextWeek;
      });
      setRecentFestivities(relevantFestivities.slice(0, 3));

    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  // Event navigation handlers
  const handleEventNavigation = (direction) => {
    if (direction === "prev" && currentEventIndex > 0) {
      setCurrentEventIndex(currentEventIndex - 1);
      
      // Also update the selectedEventId and URL
      if (allEvents[currentEventIndex - 1]) {
        setSelectedEventId(allEvents[currentEventIndex - 1].id);
        navigate(`/dashboard?event=${allEvents[currentEventIndex - 1].id}`, { replace: true });
      }
    } else if (direction === "next" && currentEventIndex < allEvents.length - 1) {
      setCurrentEventIndex(currentEventIndex + 1);
      
      // Also update the selectedEventId and URL
      if (allEvents[currentEventIndex + 1]) {
        setSelectedEventId(allEvents[currentEventIndex + 1].id);
        navigate(`/dashboard?event=${allEvents[currentEventIndex + 1].id}`, { replace: true });
      }
    }
  };

  // Handle event selection from the list
  const handleEventSelect = (eventId) => {
    // Find the event index
    const eventIndex = allEvents.findIndex(event => event.id === eventId);
    if (eventIndex >= 0) {
      setCurrentEventIndex(eventIndex);
      setSelectedEventId(eventId);
      
      // Update URL
      navigate(`/dashboard?event=${eventId}`, { replace: true });
    }
  };

  // Handle navigation back to the events list
  const handleNavigateBackToList = () => {
    // Clear selected event ID
    setSelectedEventId(null);
    
    // Also remove from localStorage to be safe
    localStorage.removeItem('selectedEventId');
    
    // Remove event parameter from URL
    navigate('/dashboard', { replace: true });
  };

  // Handle event deletion updates
  const handleEventDeleted = (eventId) => {
    setAllEvents((prev) => prev.filter((event) => event.id !== eventId));
    setUpcomingEvents((prev) => prev.filter((event) => event.id !== eventId));
    
    if (allEvents[currentEventIndex] && allEvents[currentEventIndex].id === eventId) {
      if (allEvents.length > 1) {
        const newIndex =
          currentEventIndex < allEvents.length - 1
            ? currentEventIndex
            : Math.max(0, currentEventIndex - 1);
        setCurrentEventIndex(newIndex);
        
        // If we're in the mobile detail view, navigate back to the list
        if (isMobile && selectedEventId) {
          setSelectedEventId(null);
          localStorage.removeItem('selectedEventId');
          navigate('/dashboard', { replace: true });
        }
      } else {
        setCurrentEventIndex(-1);
        setSelectedEventId(null);
        localStorage.removeItem('selectedEventId');
        navigate('/dashboard', { replace: true });
      }
    }
    
    setDeleteError("");
  };

  const handleDeleteError = (errorMessage) => {
    setDeleteError(`Napaka pri brisanju dogodka: ${errorMessage}`);
    setTimeout(() => setDeleteError(""), 5000);
  };

  // Modal handling for creating new events
  const handleCreateEvent = () => {
    setShowNewEventModal(true);
  };

  const handleCloseModal = () => {
    setShowNewEventModal(false);
  };

  const handleSaveEvent = () => {
    setShowNewEventModal(false);
    loadDashboardData();
  };

  // Calendar date selection handler
  const handleSelectDate = (date, festivityEvent = null) => {
    setSelectedDate(date);
    setFestivityEvent(null);
    const formattedDate = formatLocalDate(date);
    const eventsOnDate = allEvents.filter(
      (event) => formatLocalDate(new Date(event.date)) === formattedDate
    );
    if (eventsOnDate.length > 0) {
      const eventIndex = allEvents.findIndex((e) => e.id === eventsOnDate[0].id);
      if (eventIndex >= 0) {
        setCurrentEventIndex(eventIndex);
        
        // For mobile, also select the event to view details
        if (isMobile) {
          setSelectedEventId(eventsOnDate[0].id);
          navigate(`/dashboard?event=${eventsOnDate[0].id}`, { replace: true });
        }
      }
    }
  };

  // Calendar month change handler
  const handleMonthChange = async (newMonth) => {
    try {
      const year = newMonth.getFullYear();
      const month = newMonth.getMonth() + 1;
      const festivities = await fetchCatholicFestivities(year, month);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const nextWeek = new Date(today);
      nextWeek.setDate(today.getDate() + 7);
      const relevantFestivities = festivities.filter((festivity) => {
        const festivityDate = new Date(festivity.date);
        const threeDaysAgo = new Date(today);
        threeDaysAgo.setDate(today.getDate() - 3);
        return festivityDate >= threeDaysAgo && festivityDate <= nextWeek;
      });
      setRecentFestivities(relevantFestivities.slice(0, 3));
    } catch (error) {
    }
  };

  // Helper function to format a date for comparison
  const formatLocalDate = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
      date.getDate()
    ).padStart(2, "0")}`;
  };

  // Helper function to format a date for display
  const formatDate = (dateString) => {
    const options = { weekday: "long", year: "numeric", month: "long", day: "numeric" };
    const locale = language === 'sl' ? 'sl-SI' : 'en-US';
    return new Date(dateString).toLocaleDateString(locale, options);
  };

  const currentEvent = allEvents[currentEventIndex] || null;

  // Handle event data update from children
  const handleEventDataUpdate = (updatedEvent) => {
    if (!updatedEvent) return;
    
    setAllEvents(prev => {
      const newEvents = [...prev];
      const index = newEvents.findIndex(e => e.id === updatedEvent.id);
      if (index >= 0) {
        newEvents[index] = updatedEvent;
      }
      return newEvents;
    });
  };

  // Render empty state if there are no events
  const renderEmptyState = () => (
    <div className={`card shadow-sm ${isMobile ? 'border-0' : 'border'} rounded-4`}>
      <div className="card-body text-center p-5">
        <div className="mb-4">
          <FontAwesomeIcon icon={faCalendarAlt} className="text-muted" style={{ fontSize: "3rem" }} />
        </div>
        <h4 className="mb-3">{t('events.calendar.noEvents')}</h4>
        <p className="text-muted mb-4">{t('events.calendar.noEventsDescription')}</p>
        <button className="btn btn-primary" onClick={handleCreateEvent}>
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          {t('events.calendar.createNewEvent')}
        </button>
      </div>
    </div>
  );

  return (
    <div className={`dashboard-container ${isMobile ? 'mobile-fullwidth' : ''}`}>
      {deleteError && (
        <div className="alert alert-danger mb-3 py-2">
          {deleteError}
        </div>
      )}
      
      {isMobile ? (
        // Mobile layout - Two-step navigation
        <>
          {isLoading ? (
            <div className="text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : allEvents.length === 0 ? (
            renderEmptyState()
          ) : selectedEventId ? (
            // Show event details when an event is selected
            <MobileEventView 
              event={currentEvent}
              onEventDataUpdate={handleEventDataUpdate}
              onNavigateBack={handleNavigateBackToList}
              activeTab={mobileActiveTab}
              onTabChange={setMobileActiveTab}
              onDelete={handleEventDeleted}
            />
          ) : (
            // Otherwise show the events list
            <MobileEventsList 
              events={allEvents}
              onEventSelect={handleEventSelect}
              onCreateEvent={handleCreateEvent}
            />
          )}
        </>
      ) : (
        // Desktop layout - with consistent borders
        <div className="row">
          {/* Main events section */}
          <div className="col-12 col-lg-8 mb-4">
            {isLoading ? (
              <div className="card shadow-sm border rounded-4">
                <div className="card-body text-center p-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </div>
              </div>
            ) : allEvents.length === 0 ? (
              renderEmptyState()
            ) : (
              <div className="events-navigation mb-4">
                {currentEventIndex > 0 && (
                  <button
                    className="nav-button prev"
                    onClick={() => handleEventNavigation("prev")}
                    title={t('events.calendar.previousEvent')}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                )}
                <div className={`event-card ${currentEventIndex > 0 ? "slide-in-left" : "slide-in-right"}`}>
                  <EventCard 
                    event={currentEvent}
                    onEventDataUpdate={handleEventDataUpdate}
                    user={user}
                    onDelete={handleEventDeleted}
                  />
                </div>
                {currentEventIndex < allEvents.length - 1 && (
                  <button
                    className="nav-button next"
                    onClick={() => handleEventNavigation("next")}
                    title={t('events.calendar.nextEvent')}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                )}
                {allEvents.length > 0 && !festivityEvent && (
                  <div className="text-center mt-2">
                    <small className="text-muted">
                      {t('events.calendar.eventCountText', {
                        current: currentEventIndex + 1,
                        total: allEvents.length
                      })}
                    </small>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar section - Fixed borders for desktop */}
          <div className="col-lg-4">
            {/* Calendar - Desktop only with border */}
            <div className="card shadow-sm border rounded-4 mb-4">
              <div className="card-header bg-transparent border-bottom">
                <h5 className="mb-0">{t('events.calendar.calendarTitle')}</h5>
              </div>
              <div className="card-body p-2">
                <CalendarView
                  onSelectEvent={handleSelectDate}
                  onMonthChange={handleMonthChange}
                  events={allEvents}
                  festivities={recentFestivities}
                />
                <div className="text-center mt-3 pt-2 border-top">
                  <button className="btn btn-primary" onClick={handleCreateEvent}>
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    {t('events.calendar.createNewEvent')}
                  </button>
                </div>
              </div>
            </div>

            {/* Upcoming events - Desktop with border */}
            <div className="card shadow-sm border rounded-4 mb-4">
              <div className="card-header bg-transparent border-bottom d-flex justify-content-between align-items-center">
                <h5 className="mb-0">{t('events.calendar.upcomingEvents')}</h5>
              </div>
              <div className="card-body p-0">
                {isLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {upcomingEvents.length > 0 ? (
                      <div className="list-group list-group-flush">
                        {upcomingEvents.map((event) => (
                          <div
                            key={event.id}
                            className="list-group-item list-group-item-action px-3 py-3 border-bottom"
                            onClick={() => {
                              const eventIndex = allEvents.findIndex((e) => e.id === event.id);
                              if (eventIndex >= 0) {
                                setCurrentEventIndex(eventIndex);
                                setFestivityEvent(null);
                                window.scrollTo({ top: 0, behavior: "smooth" });
                              }
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="d-flex w-100 justify-content-between align-items-start">
                              <div className="flex-grow-1">
                                <h6 className="mb-1">{event.title}</h6>
                                <small className="text-muted d-block">
                                  {new Date(event.date).toLocaleDateString(language === 'sl' ? 'sl-SI' : 'en-US')}
                                </small>
                                {event.location && (
                                  <small className="d-block text-muted">
                                    <i className="bi bi-geo-alt me-1"></i>
                                    {event.location}
                                  </small>
                                )}
                              </div>
                              <DeleteEventButton
                                event={event}
                                onSuccess={handleEventDeleted}
                                onError={handleDeleteError}
                                buttonStyle="btn-link text-danger p-0 ms-2"
                                iconClass=""
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-muted mb-0">{t('events.calendar.noUpcomingEvents')}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Recent festivities - Desktop with border */}
            <div className="card shadow-sm border rounded-4">
              <div className="card-header bg-transparent border-bottom">
                <h5 className="mb-0">{t('events.calendar.festivities')}</h5>
              </div>
              <div className="card-body">
                {isLoading ? (
                  <div className="text-center py-3">
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : recentFestivities.length > 0 ? (
                  recentFestivities.map((festivity) => (
                    <div key={festivity.id} className="mb-3 pb-3 border-bottom">
                      <div className="d-flex justify-content-between align-items-start">
                        <h6 className="mb-1">{festivity.name}</h6>
                        <span className="badge bg-light text-dark">{formatDate(festivity.date)}</span>
                      </div>
                      {festivity.description && (
                        <p className="small text-muted mb-0">{festivity.description.substring(0, 80)}...</p>
                      )}
                    </div>
                  ))
                ) : (
                  <p className="text-center text-muted mb-0">{t('events.calendar.noChurchHolidays')}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      
      {showNewEventModal && (
        <EventFormModal onClose={handleCloseModal} onSave={handleSaveEvent} />
      )}
    </div>
  );
}

export default UserDashboard;